import { __rest } from "tslib";
import * as DialogPrimitive from '@radix-ui/react-dialog';
import { Slot } from '@radix-ui/react-slot';
import React from 'react';
import { tv } from 'tailwind-variants';
import { CloseIcon } from '@marvelapp/ballpark-icons';
import { BtwButton } from './BtwButton';
import { BtwHeading } from './BtwHeading';
import { BtwText } from './BtwText';
import { Stack } from './Stack';
/**
 * Modal overlay
 */
const overlay = tv({
    base: [
        'flex',
        'flex-col',
        'items-center',
        'justify-center',
        'max-sm:items-end',
        'z-[500]',
        'fixed',
        'inset-0',
        'data-[state=open]:motion-safe:animate-in',
        'data-[state=open]:motion-safe:fade-in',
        'data-[state=open]:motion-safe:duration-300',
        'data-[state=closed]:motion-safe:animate-out',
        'data-[state=closed]:motion-safe:fade-out',
        'data-[state=closed]:motion-safe:duration-500',
    ],
    variants: {
        position: {
            center: ['items-center', 'justify-center'],
            right: ['items-end', 'justify-center'],
        },
        background: {
            blurred: ['bg-gray-200/50', 'backdrop-blur-md'],
            none: ['bg-transparent'],
        },
    },
    defaultVariants: {
        position: 'center',
        background: 'blurred',
    },
});
const Overlay = React.forwardRef(function Item(props, forwardedRef) {
    const { className, position, background } = props, rest = __rest(props, ["className", "position", "background"]);
    return (React.createElement(DialogPrimitive.Overlay, Object.assign({ className: overlay({ className, position, background }), ref: forwardedRef }, rest)));
});
/**
 * Modal content
 */
const content = tv({
    base: [
        'bg-white',
        'shadow-lg',
        'flex',
        'flex-col',
        'w-screen',
        'max-w-lg',
        'z-40',
        'relative',
        'data-[state=open]:motion-safe:animate-in',
        'data-[state=open]:motion-safe:fade-in',
        'data-[state=open]:motion-safe:duration-200',
        'data-[state=open]:motion-safe:ease-smooth',
        'data-[state=closed]:motion-safe:animate-out',
        'data-[state=closed]:motion-safe:fade-out',
        'data-[state=closed]:motion-safe:duration-200',
        'data-[state=closed]:motion-safe:ease-smooth',
    ],
    variants: {
        position: {
            center: [
                'max-h-[80vh]',
                'rounded-t-xl',
                'sm:rounded-xl',
                'overflow-hidden',
                'data-[state=open]:motion-safe:slide-in-from-bottom-10',
                'sm:data-[state=open]:motion-safe:zoom-in-95',
                'data-[state=closed]:motion-safe:slide-out-to-bottom-10',
                'sm:data-[state=closed]:motion-safe:zoom-out-95',
            ],
            right: [
                'top-0',
                'h-full',
                'right-0',
                'data-[state=open]:motion-safe:slide-in-from-right',
                'data-[state=closed]:motion-safe:slide-out-to-right',
            ],
        },
    },
    defaultVariants: {
        position: 'center',
    },
});
const contentInner = tv({
    base: ['flex', 'flex-col', 'overflow-auto', 'gap-8', 'flex-1'],
    variants: {
        hasScrolled: {
            true: [
                '[&>.header-with-description]:ring-1',
                '[&>.header-with-description]:ring-slate-600/10',
            ],
            false: [],
        },
        justify: {
            between: ['justify-between'],
        },
    },
});
const Content = React.forwardRef(function Content(props, forwardedRef) {
    const { children, className, hasBackground = true, hasPortal = true, justify, position, asChild } = props, rest = __rest(props, ["children", "className", "hasBackground", "hasPortal", "justify", "position", "asChild"]);
    const [hasScrolled, setHasScrolled] = React.useState(false);
    const handleScroll = (event) => {
        setHasScrolled(event.currentTarget.scrollTop > 0);
    };
    const Comp = asChild ? Slot : 'div';
    return (React.createElement(WithPortal, { hasPortal: hasPortal },
        React.createElement(Overlay, { position: position, background: hasBackground ? 'blurred' : 'none' },
            React.createElement(DialogPrimitive.Content, Object.assign({ className: content({ className, position }), ref: forwardedRef }, rest),
                React.createElement(Comp, { className: contentInner({ hasScrolled, justify }), onScroll: handleScroll }, children)))));
});
function WithPortal(props) {
    const { hasPortal, children } = props;
    if (hasPortal) {
        return React.createElement(DialogPrimitive.Portal, null, children);
    }
    return React.createElement(React.Fragment, null, children);
}
/**
 * Modal close
 */
function CloseButton() {
    return (React.createElement(DialogPrimitive.Close, { asChild: true },
        React.createElement(BtwButton, { testId: "close-button", size: "sm", variant: "ghost", standaloneIcon: React.createElement(CloseIcon, null) })));
}
/**
 * Modal title
 */
const Title = React.forwardRef(function Title(props, forwardedRef) {
    return (React.createElement(DialogPrimitive.Title, { asChild: true, ref: forwardedRef },
        React.createElement(BtwHeading, Object.assign({ size: "2xl" }, props))));
});
/**
 * Modal description
 */
const Description = React.forwardRef(function Description(props, forwardedRef) {
    return (React.createElement(DialogPrimitive.Description, { asChild: true, ref: forwardedRef },
        React.createElement(BtwText, Object.assign({ leading: "relaxed" }, props))));
});
function Header(props) {
    const { title, icon } = props;
    return (React.createElement("header", { className: "sticky top-0 w-full border-b border-gray-600/10" },
        React.createElement("div", { className: "flex items-center gap-1 rounded-t-xl bg-white/90 p-4 backdrop-blur-md" },
            React.createElement(Title, { className: "flex-1", size: "base" }, title),
            icon,
            React.createElement(CloseButton, null))));
}
function BigHeader(props) {
    const { title, description } = props;
    return (React.createElement(Stack, { className: "header-with-description transition-combined ease-smooth sticky top-0 -mb-4 rounded-t-xl bg-white/90 py-4 pl-8 pr-4 backdrop-blur-md duration-300", direction: "row", gap: "4", justify: "between" },
        React.createElement(Stack, { gap: "1.5", className: "pt-4" },
            React.createElement(Title, { size: "xl" }, title),
            React.createElement(Description, { size: "sm" }, description)),
        React.createElement(CloseButton, null)));
}
function Footer(props) {
    return (React.createElement("footer", { className: "sticky bottom-0 border-t border-gray-600/10" },
        React.createElement("div", { className: "flex items-center justify-end gap-3 bg-white/90 p-4 backdrop-blur-md sm:rounded-b-xl only:[&>*]:w-full" }, props.children)));
}
/**
 * Modal body
 */
const body = tv({
    base: ['flex', 'flex-col'],
    variants: {
        size: {
            sm: ['px-6', 'gap-6'],
            base: ['px-8', 'gap-8'],
        },
    },
    defaultVariants: {
        size: 'base',
    },
});
function Body(props) {
    const { size, className } = props, rest = __rest(props, ["size", "className"]);
    return React.createElement("div", Object.assign({ className: body({ className, size }) }, rest));
}
const BtwModal = {
    Body,
    Close: DialogPrimitive.Close,
    Content,
    Description,
    Footer,
    Header,
    BigHeader,
    Overlay,
    Root: DialogPrimitive.Root,
    Title,
    Trigger: DialogPrimitive.Trigger,
};
export { BtwModal };
